import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';

import { logger } from '@core/helpers/logger';
import {
  AccountService,
  BillingService,
  MeetingsBffService,
  MeetingsService,
  ProfileService,
  SchoolsService,
} from '@core/services';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import { WebsocketService } from '@core/websocket';
import { Store } from '@ngrx/store';
// TODO разобраться с Sentry : import * as Sentry from '@sentry/angular';
import { AbstractCheckoutWizard } from '@shared/checkout-wizards/models/abstract-checkout-wizard';
import { StepByStepWizardStep } from '@shared/step-by-step-wizard';
import { getProfileSettings } from '@store/reducers/data.reducer';
import { getMyCurrency } from '@store/reducers/profile.reducer';
import {
  CSchool,
  IUserAccess,
  MeetingCheckoutRequest,
  MeetingCheckoutResponse,
  SchoolDetailsType,
  User,
  type Meeting,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, filter, of, Subject, takeUntil, throwError, zip } from 'rxjs';
import { debounceTime, mergeAll, switchMap, tap } from 'rxjs/operators';

type WizardContext = MeetingCheckoutWizardDialogComponent;
type WizardViewType = 'payment' | 'cant-buy-meeting' | 'own-meeting' | 'error';

/** Мастер для записи на митинг */
@Component({
  selector: 'app-meeting-checkout-wizard-dialog',
  templateUrl: './meeting-checkout-wizard-dialog.component.html',
  styleUrls: ['./meeting-checkout-wizard-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingCheckoutWizardDialogComponent extends AbstractCheckoutWizard<WizardContext, WizardViewType> {
  // DI
  protected readonly accountService = inject(AccountService);
  protected readonly profileService = inject(ProfileService);
  protected readonly billingV2Service = inject(BillingV2Service);
  protected readonly meetingsService = inject(MeetingsService);
  protected readonly meetingsBffService = inject(MeetingsBffService);
  protected readonly schoolService = inject(SchoolsService);
  protected readonly websocketService = inject(WebsocketService);
  protected readonly deviceService = inject(DeviceDetectorService);
  protected readonly store = inject(Store);
  protected readonly billingService = inject(BillingService);

  @Input()
  public set meetingId(value: string) {
    this._meetingId = value;
  }

  @Output()
  public closed = new EventEmitter<boolean>();

  @Output()
  public succeed = new EventEmitter<boolean>();

  public meeting: Meeting;
  public school: CSchool;
  public teacher: User;
  public userHavePaymentMethods = null;
  public payClicked = false;
  public openAddAccountModal = false;
  public addAccountProviderId: string;
  public afterAddAccountNeedUpdate: number;

  private currencyId;
  private _checkoutDetails: MeetingCheckoutResponse;
  private _isCheckout = false;
  private _userSettings: Partial<IUserAccess>;
  private reloadMeeting$$ = new Subject<boolean>();
  private _meetingMarkedForReloadLater = false;
  private _meetingLoading = false;

  protected _meetingId: string;

  public ngOnInit() {
    super.ngOnInit();

    of(...[this.watchWsReconnected$, this.getMyCurrency$, this.watchReloadMeeting$])
      .pipe(
        mergeAll(),
        tap(() => this.detectChanges()),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.getUserPaymentMethods();
    this.markMeetingForReload();
  }

  public onClose() {
    this.closed.emit(true);
  }

  /** Инициализация шагов мастера */
  protected initWizardSteps() {
    this.steps.addStep(
      new StepByStepWizardStep<WizardContext>({
        id: 'payment',
        title: 'payment-checkout-wizard.steps.user-service.payment',
        context: this,
        checks: [],
        visible: true,
        active: false,
      }),
    );
    this.cdr.detectChanges();
  }

  /** Настройка шагов мастера под набор входных параметров */
  protected setupWizardSteps() {}

  protected onLoadMeetingDone() {
    this.log(this.constructor.name + '.onLoadMeetingDone()');
    if (this.isOwnMeeting) {
      return this.activateView('own-meeting');
    }
    if (!this.meeting?.is?.paidMeeting || this.meeting?.is?.paidOrFreeParticipant) {
      // либо не надо оплачивать, либо ранее уже купил, либо процесс покупки завершился
      this.closed.emit(true);
      // ?? return this.activateView('success');
      return;
    }

    this.setupPaymentWidget();
    this.steps.goStep('payment');

    if (!this.meeting?.is?.paidAcceptRequired && !this.meeting?.is?.paidJoinRequired) {
      return this.activateView('cant-buy-meeting');
    }

    of(...[this.loadSchool$(this.meeting.school_id), this.loadTeacher$(this.meeting.author_id)])
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected markMeetingForReloadLater() {
    this.log(this.constructor.name + '.markMeetingForReloadLater', '-> marked meeting to RELOAD LATER');
    this._meetingMarkedForReloadLater = true;
  }

  protected clearMarkMeetingForReloadLater() {
    this.log(this.constructor.name + '.clearMarkMeetingForReloadLater', '-> cleared mark meeting to RELOAD LATER');
    this._meetingMarkedForReloadLater = false;
  }

  protected checkMeetingForReloadLater() {
    if (this._meetingMarkedForReloadLater) {
      this.log(
        this.constructor.name + '.checkMeetingForReloadLater',
        '-> meeting was marked to RELOAD LATER -> reload now',
      );
      this._meetingMarkedForReloadLater = false;
      this.markMeetingForReload();
    }
  }

  /** Настройка данных для проведения платежа */
  public setupPaymentWidget() {
    if (this._isCheckout || this._checkoutDetails) {
      return;
    }

    this._isCheckout = true;
    const checkoutRequest = new MeetingCheckoutRequest({
      meeting_id: this._meetingId,
      account_id: this._me?.id,
      currency_id: this._me.currency_id,
    });
    zip(this.meetingsService.getMeetingCheckoutDetails(checkoutRequest), this.store.select(getProfileSettings))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([details, settings]) => {
          this._isCheckout = false;
          this._checkoutDetails = details;
          this._userSettings = settings;

          if (!this._checkoutDetails.status) {
            logger.error(this._checkoutDetails);
            this.errorMessage = this._checkoutDetails.error;
            this.activateView('error');
            return;
          }

          const options = this._checkoutDetails.model;
          const order_id = String(options.billing_order_id);
          const charge_id = String(options.billing_charge_id);

          this.paymentOptions = {
            first_name: this._me.first_name,
            last_name: this._me.last_name,

            email: this._userSettings.email,
            phone: this._userSettings.mobile_phone,

            country: this._me.country?.alpha2Code,

            order_id,
            charge_id: [charge_id],
            meta: {
              meeting_id: this._meetingId,
              account_id: this._me?.id,
            },
          };

          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.errorMessage = error.message;
          this.activateView('error');
        },
      });
  }

  protected get isOwnMeeting(): boolean {
    return this.meeting?.author_id === this._me?.id;
  }

  /** Выполнить обработку по активации шага мастера */
  protected onStepActivated(id: string) {
    // this._debug(this.constructor.name + '.onStepActivated ' + id);
  }

  protected get watchMe$() {
    // дополнительная проверка - не давать записываться на свои услуги
    return super.watchMe$.pipe(
      tap(() => {
        if (this.isOwnMeeting) {
          return this.activateView('own-meeting');
        }
      }),
    );
  }

  protected loadMeeting$(meeting_id) {
    return this.meetingsBffService.getOne({ strategy: 'by-id', filter: { id: meeting_id } }).pipe(
      tap((meeting) => {
        this.meeting = meeting;
        if (!this.meeting) {
          this.errorMessage = 'meeting not found';
          this.activateView('error');
        }
      }),
      tap(() => this.onLoadMeetingDone()),
      catchError((err) => {
        this.errorMessage = err.message;
        this.activateView('error');
        logger.error(err);
        // TODO разобраться с Sentry : Sentry.captureException(err);
        return throwError(err);
      }),
    );
  }

  protected get getMyCurrency$() {
    return this.store.select(getMyCurrency).pipe(tap((currency) => (this.currencyId = currency)));
  }

  /** Обновление митинга при реконнекте к веб-сокетам */
  protected get watchWsReconnected$() {
    return this.websocketService.status$.pipe(
      debounceTime(1000),
      tap((isOnline) => {
        if (isOnline) {
          this.log(this.constructor.name + '.watchWsReconnected$ -> online -> markScheduleForReload()');
          this.markMeetingForReload();
        }
      }),
    );
  }

  /** Загрузить школу по id */
  protected loadSchool$(id: string) {
    if (!id || this.school?.id === id) {
      return of(null);
    }

    const details: SchoolDetailsType[] = ['id', 'title', 'cover_id', 'participants' /* 'teachers', 'stats' */];
    return this.schoolService.getSchoolByIdOrSlug(id, details, 'LOAD_TIME').pipe(
      tap((school) => {
        this.school = school;
        if (!this.school) {
          this.errorMessage = 'school not found';
          this.activateView('error');
        }
      }),
      catchError((err) => {
        this.errorMessage = err.message;
        this.activateView('error');
        logger.error(err);
        // TODO разобраться с Sentry : Sentry.captureException(err);
        return throwError(err);
      }),
    );
  }

  /** Загрузить преподавателя */
  protected loadTeacher$(id: string) {
    if (!id || this.teacher?.id === id) {
      return of(null);
    }
    return this.accountService.getUserById(id).pipe(
      tap((teacher) => (this.teacher = teacher)),
      tap(() => this.onLoadTeacherDone()),
      catchError((err) => {
        this.errorMessage = err.message;
        this.activateView('error');
        logger.error(err);
        // TODO разобраться с Sentry : Sentry.captureException(err);
        return throwError(err);
      }),
    );
  }

  protected onLoadTeacherDone() {}

  /** Поставить признак необходимости обновления митинга  */
  protected markMeetingForReload() {
    this.log(this.constructor.name + '.markScheduleForReload -> reloadSchedule$$');
    this.reloadMeeting$$.next(true);
    /** @see watchReloadMeeting$ */
  }

  protected get watchReloadMeeting$() {
    return this.reloadMeeting$$.pipe(
      debounceTime(1000),
      filter(() => {
        if (this._meetingLoading) {
          this.log(this.constructor.name + '.watchReloadMeeting$', '-> already loading');
          // не перегружать, если митинг уже в процессе загрузки
          // но запомнить, что митинг нужно будет перезагрузить позже
          this.markMeetingForReloadLater();
          return false;
        } else {
          this.log(this.constructor.name + '.watchReloadMeeting$ -> loadMeeting$');
          this._meetingLoading = true;
          this.clearMarkMeetingForReloadLater();
          return true;
        }
      }),
      switchMap(() => this.loadMeeting$(this._meetingId)),
      /** @see onLoadMeetingDone() */
      tap(() => {
        this.log(this.constructor.name + '.watchReloadMeeting$ -> loaded');
        this._meetingLoading = false;
        this.checkMeetingForReloadLater();
      }),
    );
  }

  public onPaymentSucceed() {
    this.log(this.constructor.name + '.onPaymentSucceed');
    super.onPaymentSucceed();
    this.succeed.emit(true);
  }

  public getUserPaymentMethods() {
    this.billingV2Service
      .getAccounts()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (accounts) => {
          this.userHavePaymentMethods = accounts.length > 0;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onStartPayment() {
    this.payClicked = true;
    super.onStartPayment();
    this.detectChanges();
  }

  public onAddAccount(providerId: string) {
    this.openAddAccountModal = true;
    this.addAccountProviderId = providerId;
  }

  public onCloseAddAccount() {
    this.openAddAccountModal = false;
    this.afterAddAccountNeedUpdate = Date.now();
    this.detectChanges();
  }
}
